'use client';

import { useSyncExternalStore } from 'react';

/**
 * Discover open/close state — a module store on the panel-events pattern so
 * the rail, the keyboard chord, the canvas and the overlay host all read one
 * value with no prop threading and no drift.
 *
 * The surface itself has exactly two mounts (spec: "Discover, opened" and
 * "Discover, inline"). This store only owns the OPENED overlay; the inline
 * mount is derived on the canvas from `workspaceIsEmpty` and needs no state
 * here. `initial` lets an opener land directly on a stack level (Stage's
 * "My files" opens straight into the content manager).
 */

export type DiscoverLevel =
  | { kind: 'channel'; slug: string; name: string }
  | { kind: 'post'; id: string }
  | { kind: 'files' }
  // The maker's shelf: every app they created or remixed, drafts included
  // (drafts carry the Publish CTA). AppsTab, mounted as an overlay level.
  | { kind: 'apps' }
  // One of the maker's own UNPUBLISHED apps: its manage page (Publish /
  // Share / Open / Edit), pushed from a Mine-shelf card. Published cards
  // push 'channel' instead — this is the draft's equivalent screen.
  // `manage` lands with the identity drawer already open (the card context
  // menu's Manage entry).
  | { kind: 'appDraft'; listing: import('./catalog-card').CatalogListing; manage?: boolean }
  | { kind: 'feed' }
  // The post / channel composer, one level above the feed. With Feed gone
  // from the rail (E1) this is the create path: the feed level's header
  // carries "+ Create" and pushes it, matching the old panel's inline swap.
  | { kind: 'compose'; what: 'post' | 'channel' };

/**
 * `docked`: the opened overlay is shrunk to its desktop column beside your
 * work (published by the overlay itself, discover-host.tsx), so it does NOT
 * cover the canvas. Every open starts undocked, i.e. covering, until the
 * overlay says otherwise: the canvas's ad slot reads `useDiscoverCoversCanvas`
 * and must never be live under a Discover that covers it (2026-09-30 fix
 * round), so the first render of an open errs on the covered side.
 */
type DiscoverState = { open: boolean; initial: DiscoverLevel | null; docked: boolean };

let state: DiscoverState = { open: false, initial: null, docked: false };
const subs = new Set<() => void>();
const emit = () => subs.forEach((fn) => fn());

/** Overlay-is-live level delivery: `openDiscover(level)` while the overlay is
 *  already mounted pushes the level onto its stack through this event —
 *  `initial` is only consumed at MOUNT, so without it a second deep link (an
 *  in-app /p/ /c/ click, a late URL action) opened nothing. */
export const DISCOVER_LEVEL_EVENT = 'ws:discover-level';

export function openDiscover(initial?: DiscoverLevel): void {
  const wasOpen = state.open;
  // A level-less open must not CLOBBER a queued level: the deep-link action
  // (`?discover=c:...`) and the empty-workspace auto-open both run at mount,
  // in either order — whoever carries a level wins, a plain open just makes
  // sure the surface is up. `consumeInitialLevel` still clears on read.
  // A level arriving while the overlay is LIVE is delivered by event instead
  // of queued — a queued initial would only fire on some future remount.
  state = {
    open: true,
    initial: wasOpen && initial ? state.initial : initial ?? state.initial,
    docked: wasOpen ? state.docked : false,
  };
  emit();
  if (wasOpen && initial && typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(DISCOVER_LEVEL_EVENT, { detail: initial }));
  }
}

export function closeDiscover(): void {
  if (!state.open) return;
  state = { open: false, initial: null, docked: false };
  emit();
}

/** The overlay reports whether it is docked beside your work (desktop column) or covering the canvas. */
export function setDiscoverDocked(docked: boolean): void {
  if (!state.open || state.docked === docked) return;
  state = { ...state, docked };
  emit();
}

/**
 * The rail button / ⌘⇧K entry point. Dispatched as an event rather than a
 * plain toggle because only the HOST (which has store access) can tell
 * whether the inline mount is currently showing — in that state the press
 * focuses the inline field instead of opening a second Discover over it.
 */
export const DISCOVER_TOGGLE_EVENT = 'ws:discover-toggle';
export function requestDiscoverToggle(): void {
  window.dispatchEvent(new Event(DISCOVER_TOGGLE_EVENT));
}

/** Host → inline mount: put the caret in the inline field. */
export const DISCOVER_FOCUS_EVENT = 'ws:discover-focus';
export function requestDiscoverFocus(): void {
  window.dispatchEvent(new Event(DISCOVER_FOCUS_EVENT));
}

export function consumeInitialLevel(): DiscoverLevel | null {
  const level = state.initial;
  if (level) state = { ...state, initial: null };
  return level;
}

/**
 * The catalog chip a FRESH open starts on. The empty workspace's "Find an
 * agent" (empty-workspace-hint.tsx) lands on Agents rather than Apps. A root
 * is not a level (it is which chip is pressed, not something pushed on the
 * stack), so it rides beside `initial` instead of inside it, and it is read
 * once, at the overlay's mount, exactly like `consumeInitialLevel`.
 *
 * A live overlay keeps whatever chip its user already picked: only a closed
 * Discover has a mount coming that will read this, and a root queued for an
 * open that never mounts would surface on some later, unrelated open.
 */
export type DiscoverRoot = 'apps' | 'agents';
let initialRoot: DiscoverRoot | null = null;

export function openDiscoverOn(root: DiscoverRoot): void {
  if (!state.open) initialRoot = root;
  openDiscover();
}

export function consumeInitialRoot(): DiscoverRoot | null {
  const root = initialRoot;
  initialRoot = null;
  return root;
}

const getSnapshot = () => state.open;
const getServerSnapshot = () => false;
const getCoversSnapshot = () => state.open && !state.docked;

function subscribe(cb: () => void): () => void {
  subs.add(cb);
  return () => subs.delete(cb);
}

export function useDiscoverOpen(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/** Discover is open over the canvas area (open and not docked as the desktop column beside your work). */
export function useDiscoverCoversCanvas(): boolean {
  return useSyncExternalStore(subscribe, getCoversSnapshot, getServerSnapshot);
}
